import React, {Fragment, useEffect, useLayoutEffect, useRef, useState} from "react";
import {CallbackRegistry, clearObj, debounce, funcOrValOr, getCallStack, makeArr, objMap} from "./CommonJS";

export const getElmOfRef = r =>
    typeof r === "string"? document.getElementsByClassName(r)?.[0] :
        (r?.current ?? r) instanceof Element? (r?.current ?? r) : null;

// sets a ref object, in case you need to override the ref= callback.
export const initRef = (ref, el) => typeof ref === "function"? ref(el) : ref && (ref.current = el);

// HACK: tell if current code is while component is rendering.
export const isInRender = () => getCallStack().includes("renderWithHooks");

/**
 * Improved version of useState:
 *  Returns a persistent state object.
 *  Allows direct modification of state (without using setState), even during render.
 *  setState can be used when requiring rerender. only modifies the passed keys, and only rerenders if any key actually modified.
 *  Closures always get the up-to-date state version.
 *  Also returns generic renderer() and a 2nd vals object that can be utilized.
 *
 * @param init
 @return {{vals: {}, resetState, setState, onChange, state, rerender: (function(): void)}}
 * setState with empty object clears all state object.
 * setState with a cb function will call the cb with the current state to get a new state change.
 * setState returns true if values changed and rerender applied.
 * resetState will reapply init object values. If no init object or empty, state will be cleared. If init is a function, it should return an init object.
 */
export function useStateEx(init = {}) {
    const rerender = useRerender();
    const vals = useRef({}).current;

    if (componentInit(vals)) Object.assign(vals, {
        state: Object.assign({}, funcOrValOr(init)),
        setStateInRender: obj => {
            obj = funcOrValOr(obj, vals.state);         // allow setState to receive a function which called with the current state and returns the new state.
            if (!Object.keys(obj).length && Object.keys(vals.state).length)
                clearObj(vals.state);
            else {
                if (Object.entries(obj).every(([k, v]) => vals.state[k] === v)) return false;
                Object.assign(vals.state, obj);
            }
            return true;
        },
        setState: obj => vals.setStateInRender(obj) && (rerender(), true),
        resetState: () => setState(funcOrValOr(init)),
        onChange: (f,v) => setState({[f]: v}),      // simplify migration from useInputChange
    });

    const {state, setState, setStateInRender, onChange, resetState} = vals;
    return {state, setState, setStateInRender, onChange, resetState, rerender, vals};
}

/**
 * @param className override className string passed by caller
 * @param optClasses strings | booleans variable number of parameters to check vs className. if basename before '-' exists in className, it will be skipped.
 * @return {string}  joined className string of non-overlapping classes + caller className
 */
export const tailCheck = (className, ...optClasses) =>
    optClasses.filter(c => c && typeof c === "string" &&
        !(new RegExp(`(?<!:)\\b${c.replace(/[()\[\]]/g, "").before('-').before(' ')}\\b`).test(className)))
        .concat(className || "")
        .join(" ");

export const flexRow = (className, ...children) =>
    <div className={`flex items-center ${className}`}>
        {children.filter(a => a || a === "" || a === 0)
            .map((a, i) => <Fragment key={i}>{a}</Fragment>)}
    </div>;

export function useRerender() {
    const [, forceRerender] = useState(0);
    return skipIfInRender => (!skipIfInRender || !isInRender()) && forceRerender(tick => tick + 1);
}

// Returns true if component runs first time or if any deps(endencies) change.
//
export function componentInit(vals, deps = [], kInit) {
    kInit = kInit ?? "_compInit";
    if (vals[kInit]?.equals(deps))
        return false;
    vals[kInit] = deps;
    return true;
}

const dataSingletons = {};
/**
 * A React mechanism to reuse initialized data once across any future usage in session.
 * @param key
 * @param fetcher - initializer func, getting callback as parameter, to call when got value.
 * @param resetOnLastUnuse? - if true, when last user of this singleton key destructs, the value of the singleton will revert to undefined.
 * @param onCtor? - function(fetcher)? issued after first subscriber registers.
 * @param onDtor? - function(fetcher)? issued after last subscriber unregisters.
 * @return {{val: unknown, refetch: CallbackRegistry.refetch, setVal: ((function(*): *)|*)}}
 */
export function useDataSingleton({key, fetcher, resetOnLastUnuse, onCtor, onDtor}) {
    const regObj = dataSingletons[key] || (dataSingletons[key] = new CallbackRegistry(fetcher));
    const [val, setVal] = useState(regObj.getCur());

    useEffect(() => {
        const unsubscribe = regObj.subscribe(setVal);           // subscribe returns unsubscribe function
        if (regObj.subs.length === 1)
            onCtor?.(regObj.refetch);
        return () => {
            unsubscribe();
            if (resetOnLastUnuse && !regObj.subs.length) {
                delete dataSingletons[key];
                onDtor?.(regObj.refetch);
            }
        }
    }, []);

    return {val, setVal: regObj.setVal, refetch: regObj.refetch};
}


// captureOrOpts: if boolean - means capture. otherwise object:
//      capture: boolean : dispatch listener before any EventTarget beneath it in the DOM tree. default false.
//      once: boolean : listener removed automatically after first invocation. default false.
// cb can be an ad hoc function - original cb is retained in the remove closure parameters.
//
export function useEventListener({eOn = window, sEv, cb, captureOrOpts, isEnabled = true}) {
    const vals = useRef({}).current;

    useEffect(() => {
        if (!isEnabled) vals.remove?.();
        else if (!vals.remove) {
            eOn?.addEventListener(sEv, cb, captureOrOpts);
            vals.remove = () => {
                eOn?.removeEventListener(sEv, cb, captureOrOpts);
                vals.remove = undefined;
            }
        }
        return vals.remove;
    }, [!!isEnabled]);
}

export function useDebounce(cb, delay, opts = {}) {
    const vals = useRef({}).current;
    if (componentInit(vals))
        vals.debounced = debounce(cb, delay, opts);
    useEffect(() => () => vals.debounced.cancel(), []);
    return vals.debounced;
}


// example: useObserver(ResizeObserver, rTbl, ["height"], calcSizes, {noInit: 1, debounceMs: 100})
// opts:
//  noInit: unless positive, after initial html layout, cb will be called.
//  debounceMs: number of millis to debounce cb call, unless not called in last debounceMs millis.
//
export function useObserver(observer, el, metrics, cb, opts = {}) {
    if (!Array.isArray(metrics)) metrics = [metrics];

    const debounced = useDebounce(cb, opts.debounceMs ?? 0, {leading: 1});

    useLayoutEffect(() => {
        const prevVals = {};
        const hObserver = new observer(changes => {
            const isInit = !prevVals.hasOwnProperty(metrics[0]);
            if (metrics.every(m => changes[0].contentRect[m] === prevVals[m])) return;
            Object.assign(prevVals, objMap(metrics, m => changes[0].contentRect[m]));
            if (!opts.noInit || !isInit) debounced();
        });
        hObserver.observe(getElmOfRef(el));

        return () => hObserver.disconnect();            // stop listening to changes
    }, []);
}


const c_defaultTheme = "dark";

export function useColorModeTheme() {
    const {val: curTheme, setVal} = useDataSingleton({
        key: "_app_theme",
        fetcher: onFetch => onFetch(applyTheme(localStorage.theme ?? c_defaultTheme)),
    });

    function applyTheme(theme) {
        theme = theme || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "");
        if (localStorage.theme)
            document.documentElement.classList.remove(localStorage.theme);
        if (theme)
            document.documentElement.classList.add(theme);
        document.documentElement.setAttribute("data-theme", theme || c_defaultTheme);
        return localStorage.theme = theme;
    }

    // current themes: "light" : "dark" : "" (system default))
    return {curTheme, setTheme: theme => setVal(applyTheme(theme))}
}


// Returns a function that tells if the component is still mounted.
//
export function useIsMounted() {
    const vals = useRef({}).current;
    if (!vals.isMounted)
        vals.isMounted = () => !vals.isDestroyed;

    useEffect(() => () => void (vals.isDestroyed = true), []);

    return vals.isMounted;
}

// Encapsulate a useFoo react hook into a component.
// Only useful if you want to encapsulate a useFunc as a subcomponent rather than rerender the parent.
// E.g. use at the root of the app, which also has a React.router which applies a Suspense promise on every render.
//      e.g.: <UseInComponent use={useDialogs} ctrl={appCtx} toJsx={ctrl => ctrl.dialogsJsx}/>
//
export function UseAsComponent({use, param, ctrl, toJsx}) {
    Object.assign(ctrl, use(param));
    return toJsx(ctrl);
}


export const transitions = {
    wipeDown: {enterFrom: "[clip-path:inset(0_0_100%_0)]", enterTo: "transition-all duration-100 ease-out [clip-path:inset(0_0_0_0)]"},
    wipeDownHide: {enterFrom: "[clip-path:inset(0_0_100%_0)]", enterTo: "transition-all duration-200 ease-out [clip-path:inset(0_0_0_0)]", leaveTo: "scale-90 opacity-0"},
    slideLeft: {enterFrom: "transform translate-x-full", enterTo: "translate-x-0"},
    fade100: {enterFrom: "opacity-0", enterTo: "transition-all duration-100 ease-out opacity-100"},
    dftBase: "transition-all duration-200 ease-out",
    dftFrom: "scale-90 opacity-0",
    dftTo: "scale-100 opacity-100",
};

const arrowBase = "rounded py-2 flex flex-col px-3 z-10 items-center gap-1 text-xs whitespace-nowrap text-center shadow-sm text-title after:absolute after:w-0 after:h-0 after:bottom-full "
export const arrowPlacementClasses = {
    left: arrowBase + "after:left-0 after:border-l-8 after:border-l-transparent after:border-r-8 after:border-r-transparent after:border-b-8 ml-[-1rem]",
    right: arrowBase + "after:right-0 after:border-r-8 after:border-r-transparent after:border-l-8 after:border-l-transparent after:border-b-8 mr-[-1rem]",
    middle: arrowBase + "after:left-1/2 after:border-l-8 after:border-l-transparent after:border-r-8 after:border-r-transparent after:border-b-8 ml-[-1rem]"
}

/**
 * Manage element transition effect stages.
 * @param transition - boolean|{enterFrom, enterTo, leaveFrom?, leaveTo?}: classes to apply depending on stage. If true or any class string missing, use default.
 * @param isShown - boolean?: if element should be shown or hidden.
 * @param onAfter - function(event, stage)?: callback to issue on onTransitionEnd events.
 * @return {{classNameTrans: string, refTrans: function, propsTrans}}
 */
export function useEffectTransition({transition, isShown, onAfter}) {
    const vals = useRef({stage: 0}).current;
    const rerender = useRerender();

    useEffect(() => {
        if (!transition || (!vals.stage && !isShown)) return;
        vals.stage++;
        rerender();
    },[!isShown]);

    if (isShown && vals.stage !== 2) vals.stage = 1;
    else if (!isShown && vals.stage && vals.stage !== 4) vals.stage = 3;

    const leaveTo = transition?.leaveTo ?? transition?.enterFrom;

    const classNameTrans = !transition? isShown && " " : [
        // 0: !isShown, dormant.
        () => "",
        // 1: isShown, pre-transition
        () => transition?.enterFrom ?? transitions.dftFrom,
        // 2: isShown, start-transition
        () => `${!transition.enterTo?.includes("transition-") && transitions.dftBase} ${transition.enterTo ?? transitions.dftTo}`,
        // 3: !isShown, pre-transition
        () => transition?.leaveFrom ?? transition.enterTo ?? transitions.dftTo,
        // 4: !isShown, start-transition
        () => `${!leaveTo?.includes("transition-") && transitions.dftBase} ${leaveTo ?? transitions.dftFrom}`
    ][vals.stage]();

    function onTransitionEnd(e) {
        if (e.target !== vals.elm) return;
        onAfter?.(e, vals.stage);
        if (vals.stage !== 4) return;
        vals.stage = 0;
        rerender();
    }

    return {
        classNameTrans,
        propsTrans: transition && {onTransitionEnd},
        refTrans: e => vals.elm = e
    };
}

export function Transition({show, transition, children, onAfter, className}) {
    const {classNameTrans, propsTrans, refTrans} = useEffectTransition({transition, isShown: show, onAfter});

    return !classNameTrans? null :
        <div ref={refTrans} className={`${classNameTrans} ${className}`} {...{...propsTrans, children}}/>;
}


/**
 * controls opening and closing of a variable element with an animation transition.
 * Allows the element to be removed from view when leaveTo transition finishes.
 * @param dispPanel: (function)
 * @return {{panelJsx: JSX, setPanel: ({Elm: reactElement, (props: *)}) => void}}
 *   panelJsx: a JSX that needs to be applied in the caller's render function.
 *
 *   setPanel({Elm, obj, ...[any Dialog's params]}):
 *      to open the dialog, set Elm to your dialog component, and obj to a non-falsy value.
 *      to close, set obj to a falsy value, without modifying Elm.
 *
 * e.g: const useSidePanel = () => useDynamicPanels(({panelId, ...props}) => <SidePanel key={panelId} {...props}/>);
 */
export function useDynamicPanels(dispPanel) {
    const {state: st, rerender} = useStateEx({panels: []});

    function setPanel({panelId, ...props}) {
        panelId = panelId ?? st.panels.length;
        props = {...st.panels[panelId]?.props, hide: props.hide, ...props};
        const onClose = _ => setPanel({panelId, hide: true});
        const onHideDone = _ => setPanel({panelId, Elm: null});
        st.panels[panelId] = props.Elm? {props, jsx: dispPanel({panelId, onClose, ...props, onHideDone})} : null;
        st.panelsJsx = st.panels.filter(Boolean).map(p => p.jsx);
        rerender();
        return panelId;
    }

    return {setPanel, panelsJsx: st.panelsJsx,
        isPanelOn: panelId => st.panels[panelId] && !st.panels[panelId].props.hide,
        closePanel: panelId => setPanel({panelId, hide: true}),
    };
}

export function OverlayUnderpanel({classNameOverlay, classNameTrans, onClose, children}) {
    const rOverlay = useRef();
    return !classNameTrans? null :
        !classNameOverlay?
            children() :
            <div
                className={`fixed inset-0 bg-opacity-30 z-[100] ${classNameOverlay} ${classNameTrans}`}
                ref={rOverlay} onClick={e => e.target === rOverlay.current && !onClose.cbIgnoreOutsideClicks?.() && onClose(e)}>
                {children()}
            </div>;
}

export const useScreenSize = () => {
    const getScreenSize = () => ({width: window.innerWidth, height: window.innerHeight});
    const [screenSize, setScreenSize] = useState(getScreenSize());
    useEventListener({sEv: "resize", cb: () => setScreenSize(getScreenSize())});
    return screenSize;
};

export const useBoxSize = (initialState, dep) => {
    const [boxSize, setBoxSize] = useState(initialState);
    const boxRef = useRef();

    useEffect(() => {
        const handleResize = () => boxRef?.current && setBoxSize({height: boxRef.current.offsetHeight || initialState.height, width: boxRef?.current?.offsetWidth || initialState.width});
        handleResize()
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [boxRef?.current?.offsetHeight, boxRef?.current?.offsetWidth]);

    return {boxRef, boxSize}
}


// Allows to align column widths of elements in table like list.
// Width of all elements in a column will be fixed to the one with the max scrollWidth.
// Set className of each element playing as a column to col-<number> starting 1.
//
export function fixColumnWidths(parent, nCols) {
    const cols = makeArr(nCols).map(iCol => [...(getElmOfRef(parent) ?? document).getElementsByClassName(`col-${iCol+1}`)]);
    const widths = cols.map(elems => elems.reduce((a,b) => Math.max(a, b.scrollWidth), 0));
    cols.forEach((elems, iCol) => elems.forEach(el => el.style.minWidth = `${widths[iCol]}px`));
}
